import type { Tool, ToolCategory } from '@/types';

/** Normalise Arabic text so search ignores diacritics and letter variants. */
export function normalizeArabic(value: string): string {
  return (
    value
      .trim()
      .toLowerCase()
      // Compose first: text pasted from PDFs and CMSes often arrives decomposed
      // (bare alef + a combining hamza rather than a single glyph), which the
      // letter folds below would otherwise miss entirely.
      .normalize('NFC')
      // Strip tashkeel, the combining hamza/maddah marks, the dagger alef and
      // the tatweel elongation mark. The range runs to U+065F; stopping at
      // U+0652 would leave U+0653-U+0655 in place.
      .replace(/[ً-ٰٟـ]/g, '')
      // Fold alef variants, taa marbuta and alef maqsura to a single form.
      .replace(/[آأإ]/g, 'ا')
      .replace(/ة/g, 'ه')
      .replace(/ى/g, 'ي')
  );
}

export interface ToolFilter {
  readonly category: ToolCategory | null;
  readonly query: string;
}

/** Apply the category chip and the free-text query to the tool catalogue. */
export function filterTools(tools: readonly Tool[], filter: ToolFilter): Tool[] {
  const query = normalizeArabic(filter.query);

  return tools.filter((tool) => {
    if (filter.category && tool.category !== filter.category) return false;
    if (!query) return true;

    return (
      normalizeArabic(tool.name).includes(query) ||
      normalizeArabic(tool.category).includes(query) ||
      normalizeArabic(tool.description).includes(query)
    );
  });
}
